import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PasswordInput } from "@/components/site/PasswordInput";
import {
  employerClaim,
  employerSignup,
  fetchPublicPreview,
  type PublicPreview,
} from "@/lib/employer-api";

export const Route = createFileRoute("/employer-signup")({
  validateSearch: (search: Record<string, unknown>): { ref?: string } =>
    typeof search["ref"] === "string" && search["ref"] ? { ref: search["ref"] as string } : {},
  head: () => {
    const t = "Create your employer account | CLOSDD";
    const d =
      "Create a free CLOSDD employer account to see who applied to your role, listen to their 60-second pitches and manage your hiring.";
    return {
      meta: [
        { name: "robots", content: "noindex,nofollow" },
        { title: t },
        { name: "description", content: d },
        { property: "og:title", content: t },
        { property: "og:description", content: d },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
    };
  },
  component: EmployerSignupPage,
});

const inputClass =
  "w-full rounded-xl border border-ink/12 bg-white px-4 py-3 text-[15px] text-ink outline-none transition focus:border-purple-strong";
const label = "text-[11px] font-semibold uppercase tracking-[0.14em] text-ink/45";
const btn =
  "inline-flex w-full items-center justify-center rounded-xl bg-purple-strong px-5 py-3.5 text-[13px] font-semibold text-white transition-all hover:bg-purple-mid hover:shadow-[0_14px_30px_-14px_rgba(13,27,46,0.6)] disabled:opacity-60";

function EmployerSignupPage() {
  const navigate = useNavigate();
  const { ref } = Route.useSearch();

  const [preview, setPreview] = useState<PublicPreview | null>(null);
  const [previewChecked, setPreviewChecked] = useState(!ref);
  const [company, setCompany] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [credentials, setCredentials] = useState<{ email: string; password: string } | null>(null);

  useEffect(() => {
    if (!ref) return;
    fetchPublicPreview(ref).then((found) => {
      setPreview(found);
      setPreviewChecked(true);
    });
  }, [ref]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setSubmitting(true);
    try {
      if (ref) {
        await employerClaim({ ref, contactName, email, phone, password });
        await navigate({ to: "/employer-preview" });
        return;
      }
      const { password: generated } = await employerSignup({ company, contactName, email, phone });
      setCredentials({ email, password: generated });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!previewChecked) {
    return <div className="min-h-screen bg-[#f6f8fc]" />;
  }

  if (ref && !preview) {
    return (
      <div className="min-h-screen bg-[#f6f8fc]">
        <header className="border-b border-white/10 bg-soft-black">
          <div className="mx-auto flex w-full max-w-[1120px] items-center justify-between gap-6 px-5 py-3.5">
            <Link to="/" className="font-serif text-[16px] font-bold tracking-[-0.01em] text-white">
              CLOSDD
            </Link>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[640px] px-5 py-24 text-center">
          <h1 className="font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink">This link is not active.</h1>
          <p className="mt-4 text-[17px] text-ink/65">
            It may have already been used to create an account, or expired. Email info@closdd.com and we will help you.
          </p>
          <Link
            to="/login"
            className="mt-8 inline-flex rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white"
          >
            Log in
          </Link>
        </main>
      </div>
    );
  }

  if (preview?.employer.hasAccount) {
    return (
      <div className="min-h-screen bg-[#f6f8fc]">
        <main className="mx-auto w-full max-w-[640px] px-5 py-24 text-center">
          <h1 className="font-serif text-[25px] leading-[1.05] tracking-[-0.02em] text-ink">
            {preview.employer.company} already has an account.
          </h1>
          <p className="mt-4 text-[17px] text-ink/65">Log in to see your candidates.</p>
          <Link
            to="/login"
            className="mt-8 inline-flex rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white"
          >
            Log in
          </Link>
        </main>
      </div>
    );
  }

  if (credentials) {
    return (
      <div className="min-h-screen bg-[#f6f8fc]">
        <main className="mx-auto flex min-h-screen w-full max-w-[640px] flex-col items-center justify-center px-5 py-16 text-center">
          <p className={label}>Account created</p>
          <h1 className="mt-4 font-serif text-[26px] leading-[1.05] tracking-[-0.02em] text-ink">
            Your details have been emailed to you.
          </h1>
          <p className="mt-4 text-[16px] leading-[1.65] text-ink/65">
            Here is your email and password. Log in any time to manage your roles and review applications.
          </p>
          <div className="mt-8 grid w-full gap-4 sm:grid-cols-2">
            <div className="rounded-[22px] bg-purple-soft/60 p-6">
              <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-purple-strong">Email</p>
              <p className="mt-2 break-all font-serif text-[17px] text-ink">{credentials.email}</p>
            </div>
            <div className="rounded-[22px] bg-purple-soft/60 p-6">
              <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-purple-strong">Password</p>
              <p className="mt-2 break-all font-serif text-[17px] text-ink">{credentials.password}</p>
            </div>
          </div>
          <Link to="/employer" className={`${btn} mt-8 w-fit px-8`}>
            Go to your dashboard
          </Link>
        </main>
      </div>
    );
  }

  const heading = preview
    ? `Hear the ${preview.candidates.length || ""} salespeople we found for ${preview.job.title}.`
    : "You have applications waiting. Create your account to open them.";

  return (
    <div className="min-h-screen bg-[#f6f8fc]">
      <header className="border-b border-white/10 bg-soft-black">
        <div className="mx-auto flex w-full max-w-[1120px] items-center justify-between gap-6 px-5 py-3.5">
          <Link to="/" className="font-serif text-[16px] font-bold tracking-[-0.01em] text-white">
            CLOSDD
          </Link>
          <Link to="/login" className="text-[13px] font-semibold text-white/70 transition hover:text-white">
            Already have an account? Log in
          </Link>
        </div>
      </header>

      <main className="mx-auto grid w-full max-w-[1120px] gap-6 px-5 py-10 lg:grid-cols-[1.05fr_0.95fr]">
        <form
          onSubmit={submit}
          className="rounded-2xl border border-ink/[0.07] bg-white px-6 py-7 shadow-[0_1px_2px_rgba(13,27,46,0.04),0_18px_44px_-28px_rgba(13,27,46,0.22)] sm:px-8"
        >
          <p className={label}>{preview ? preview.job.company : "Employer account"}</p>
          <h1 className="mt-2 font-serif text-[22px] leading-[1.1] tracking-[-0.02em] text-ink sm:text-[26px]">
            {heading}
          </h1>
          <p className="mt-3 text-[15px] leading-[1.65] text-ink/60">
            {preview
              ? "Create your account to hear every pitch, read the voice assessments and manage this role."
              : "Free to create your account. Post a role once you are in."}
          </p>

          <div className="mt-7 grid gap-5 sm:grid-cols-2">
            {!preview ? (
              <label className="grid gap-2 sm:col-span-2">
                <span className={label}>Company name</span>
                <input required value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Northline Group" className={inputClass} />
              </label>
            ) : null}
            <label className="grid gap-2">
              <span className={label}>Your name</span>
              <input required value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Alex Morgan" className={inputClass} />
            </label>
            <label className="grid gap-2">
              <span className={label}>Phone</span>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="07700 900000" className={inputClass} />
            </label>
            <label className="grid gap-2 sm:col-span-2">
              <span className={label}>Work email</span>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputClass} />
            </label>
            {ref ? (
              <label className="grid gap-2 sm:col-span-2">
                <span className={label}>Password</span>
                <PasswordInput
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className={`${inputClass} pr-11`}
                />
              </label>
            ) : null}
          </div>

          {error ? <p className="mt-5 text-[14px] font-semibold text-red-600">{error}</p> : null}

          <button type="submit" disabled={submitting} className={`${btn} mt-8`}>
            {submitting ? "Creating account…" : ref ? "Create account and view applications" : "Create account"}
          </button>
          <p className="mt-4 text-[13px] leading-[1.6] text-ink/50">
            Free to create. Full names, contact details, CVs and voice assessments unlock on the hiring plan.
          </p>
        </form>

        <aside className="relative overflow-hidden rounded-2xl bg-soft-black px-6 py-8 sm:px-8">
          <div className="pointer-events-none absolute inset-0 opacity-60">
            <div className="absolute -left-[15%] top-0 h-[320px] w-[320px] rounded-full bg-purple-strong/25 blur-[110px]" />
            <div className="absolute -right-[10%] bottom-0 h-[280px] w-[280px] rounded-full bg-purple-mid/20 blur-[90px]" />
          </div>
          <div className="relative">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45">What you get</p>
            <h2 className="mt-3 font-serif text-[20px] leading-[1.15] tracking-[-0.02em] text-white sm:text-[22px]">
              Stop searching through CVs.
            </h2>
            <ul className="mt-6 grid gap-4 text-[15px] leading-[1.6] text-white/70">
              {[
                "Hear a 60-second pitch from every applicant.",
                "See experience, location and salary expectations at a glance.",
                "Shortlist the people worth your time.",
                "Manage, edit and close your roles in one console.",
              ].map((line) => (
                <li key={line} className="flex gap-3">
                  <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-purple-mid" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </main>
    </div>
  );
}
